import React, { useState, useEffect } from "react";
import "./Header.css";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/images/logo.png";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HelpIcon from "@mui/icons-material/Help";
import DehazeIcon from "@mui/icons-material/Dehaze";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import XIcon from "@mui/icons-material/X";
import { fetchAPI, fetchUserAppAPI } from "../utils/api";
import PakagesData from "../layout/PakagesData";
import LoginModal from "./LoginModal";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);

  const [productCartCount, setProductCartCount] = useState(0);
  const [servicesCartCount, setServicesCartCount] = useState(0);
  const [activeCartType, setActiveCartType] = useState("product");

  // Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState({
    products: [],
    doctors: [],
    packages: [],
  });
  const [allProducts, setAllProducts] = useState([]);

  // Prefetch products for search autocomplete
  useEffect(() => {
    fetchAPI("EquipmentsController/products", {
      method: "POST",
      body: JSON.stringify({}),
    })
      .then((res) => {
        if (res && res.status === 200 && Array.isArray(res.result)) {
          setAllProducts(res.result);
        }
      })
      .catch((err) => console.error("Error fetching search products:", err));
  }, []);

  // Fetch / filter suggestions dynamically for products, doctors & packages
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions({ products: [], doctors: [], packages: [] });
      setLoadingSuggestions(false);
      return;
    }

    const q = searchTerm.toLowerCase();
    setLoadingSuggestions(true);

    const timer = setTimeout(async () => {
      // 1. Products search
      const matchedProducts = allProducts
        .filter(
          (p) =>
            p.me_product_name && p.me_product_name.toLowerCase().includes(q),
        )
        .slice(0, 4);

      // 2. Packages search
      const matchedPackages = PakagesData.filter(
        (pkg) =>
          (pkg.pakagename && pkg.pakagename.toLowerCase().includes(q)) ||
          (pkg.nummbertest && pkg.nummbertest.toLowerCase().includes(q)),
      ).slice(0, 4);

      // 3. Doctors search
      let matchedDoctors = [];
      try {
        const docRes = await fetchAPI("DoctorController/doctors", {
          method: "POST",
          body: JSON.stringify({ search: searchTerm }),
        });
        if (docRes && docRes.status === 200 && Array.isArray(docRes.result)) {
          matchedDoctors = docRes.result.slice(0, 4);
        }
      } catch (e) {
        console.error("Error fetching doctor suggestions:", e);
      }

      setSuggestions({
        products: matchedProducts,
        doctors: matchedDoctors,
        packages: matchedPackages,
      });
      setLoadingSuggestions(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, allProducts]);

  // Sync search input with URL search param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("search");
    if (q) {
      setSearchTerm(q);
    }
  }, [location.search]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) return;

    setShowSuggestions(false);
    const query = encodeURIComponent(trimmed);
    const q = trimmed.toLowerCase();

    const matchedProducts = allProducts.filter(
      (p) => p.me_product_name && p.me_product_name.toLowerCase().includes(q),
    );
    const matchedPackages = PakagesData.filter(
      (pkg) =>
        (pkg.pakagename && pkg.pakagename.toLowerCase().includes(q)) ||
        (pkg.nummbertest && pkg.nummbertest.toLowerCase().includes(q)),
    );

    if (
      suggestions.doctors.length > 0 &&
      matchedProducts.length === 0 &&
      matchedPackages.length === 0
    ) {
      navigate(`/doctor-consultation-services?search=${query}`);
    } else if (matchedPackages.length > 0 && matchedProducts.length === 0) {
      navigate(`/package?search=${query}`);
    } else {
      navigate(`/product-on-rent?search=${query}`);
    }
  };

  const updateCartCounts = () => {
    try {
      const prodSaved = localStorage.getItem("selectedProductCart");
      if (prodSaved) {
        const parsed = JSON.parse(prodSaved);
        if (Array.isArray(parsed)) setProductCartCount(parsed.length);
        else setProductCartCount(0);
      } else {
        setProductCartCount(0);
      }

      const servSaved =
        localStorage.getItem("selectedServicesCart") ||
        localStorage.getItem("selectedShortTerm");
      if (servSaved) {
        const parsed = JSON.parse(servSaved);
        if (Array.isArray(parsed)) {
          const shortTermOnly = parsed.filter(
            (item) =>
              item.type !== "longterm" && item.service_type !== "longterm",
          );
          setServicesCartCount(shortTermOnly.length);
        } else setServicesCartCount(0);
      } else {
        setServicesCartCount(0);
      }

      const active = localStorage.getItem("activeCartType") || "product";
      setActiveCartType(active);
    } catch (e) {
      setProductCartCount(0);
      setServicesCartCount(0);
    }
  };

  useEffect(() => {
    updateCartCounts();
    window.addEventListener("cartUpdated", updateCartCounts);
    return () => {
      window.removeEventListener("cartUpdated", updateCartCounts);
    };
  }, []);

  const [showLoginModal, setShowLoginModal] = useState(false);

  const checkAndLoadUser = () => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        fetchAddresses(parsedUser.user_id);
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    } else {
      setUser(null);
      setAddresses([]);
      setSelectedAddr(null);
    }
  };

  useEffect(() => {
    checkAndLoadUser();

    const handleOpenModal = () => setShowLoginModal(true);
    const handleUserStateChange = () => checkAndLoadUser();

    window.addEventListener("openLoginModal", handleOpenModal);
    window.addEventListener("userStateChange", handleUserStateChange);

    return () => {
      window.removeEventListener("openLoginModal", handleOpenModal);
      window.removeEventListener("userStateChange", handleUserStateChange);
    };
  }, [location]);

  const fetchAddresses = async (userId) => {
    try {
      const response = await fetchUserAppAPI("list-address", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });
      if (
        response &&
        response.status === 200 &&
        Array.isArray(response.result)
      ) {
        setAddresses(response.result);

        // Find if an address is already selected in localStorage
        const savedSel = localStorage.getItem("selectedAddress");
        if (savedSel) {
          try {
            const parsedSel = JSON.parse(savedSel);
            const exists = response.result.find(
              (a) => a.address_id === parsedSel.address_id,
            );
            if (exists) {
              setSelectedAddr(exists);
              window.dispatchEvent(new Event("addressChanged"));
              return;
            }
          } catch (e) {}
        }

        // Fallback to default address (or first address)
        if (response.result.length > 0) {
          const defaultAddr =
            response.result.find((a) => a.address_default == 1) ||
            response.result[0];
          setSelectedAddr(defaultAddr);
          localStorage.setItem("selectedAddress", JSON.stringify(defaultAddr));
          window.dispatchEvent(new Event("addressChanged"));
        } else {
          setSelectedAddr(null);
          localStorage.removeItem("selectedAddress");
        }
      }
    } catch (err) {
      console.error("Error fetching addresses in Header:", err);
    }
  };

  const handleSelectAddress = (addr) => {
    setSelectedAddr(addr);
    localStorage.setItem("selectedAddress", JSON.stringify(addr));
    window.dispatchEvent(new Event("addressChanged"));
  };

  const caregiverActive = [
    "/home-nursingcare",
    "/home-physiotherapy",
    "/home-bedsideattendant",
  ].includes(location.pathname);

  const diagnosticActive = ["/AllPartners", "/package"].includes(
    location.pathname,
  );

  const icuActive = [
    "/icu-installation-at-home",
    "/icu-installation-at-hospital",
  ].includes(location.pathname);

  const medicalActive = ["/product-on-rent", "/medicine"].includes(
    location.pathname,
  );

  return (
    <>
      <div className="header-mian">
        <div className="header-uper">
          <nav className="navbar navbar-expand-lg bg-body-tertiary">
            <div className="container-fluid">
              <a className="navbar-brand header_logo" href="/">
                <img src={logo} alt="Logo" />
              </a>

              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarNav"
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div className="collapse navbar-collapse" id="navbarNav">
                {/* <ul className="navbar-nav ">
                  <li className="nav-item">
                    <div className="location-header">
                      <div className="icon-location">
                        <LocationOnIcon />
                      </div>

                      <div className="dropdown">
                        <button
                          className="location-btn"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <div className="location-title">
                            {selectedAddr ? selectedAddr.address_title : "Select Location"}
                            <KeyboardArrowDownIcon />
                          </div>
                          <div className="location-address" style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {selectedAddr 
                              ? `${selectedAddr.address_name}, ${selectedAddr.address_plot_no ? selectedAddr.address_plot_no + ', ' : ''}${selectedAddr.address_locility}`
                              : "No address selected"
                            }
                          </div>
                        </button>

                        <ul className="dropdown-menu">
                          {addresses.length === 0 ? (
                            <li>
                              <Link className="dropdown-item" to={user ? "/addressbook" : "/login"}>
                                {user ? "+ Add New Address" : "Login to view addresses"}
                              </Link>
                            </li>
                          ) : (
                            <>
                              {addresses.map((addr) => (
                                <li key={addr.address_id}>
                                  <button
                                    className={`dropdown-item ${selectedAddr?.address_id === addr.address_id ? "active" : ""}`}
                                    onClick={() => handleSelectAddress(addr)}
                                    type="button"
                                    style={{ textAlign: "left", width: "100%", border: "none", background: "none" }}
                                  >
                                    <strong style={{ textTransform: "capitalize" }}>{addr.address_title}</strong>: {addr.address_name}, {addr.address_city_name || addr.address_city}
                                  </button>
                                </li>
                              ))}
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </li>
                </ul> */}

                <div
                  className="serch-form-header position-relative"
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setShowSuggestions(false);
                    }
                  }}
                >
                  <form className="search-form" onSubmit={handleSearchSubmit}>
                    <input
                      type="search"
                      placeholder="Search Products, Doctors, Packages.........."
                      className="search-input"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                    />
                    <button type="submit" className="search-btn" title="Search">
                      <SearchIcon />
                    </button>
                  </form>

                  {/* Auto-suggest Live Search Results Dropdown */}
                  {showSuggestions && searchTerm.trim().length > 0 && (
                    <div className="search-suggestions-dropdown shadow-lg bg-white rounded-3 p-3">
                      {loadingSuggestions ? (
                        <div className="text-center py-3 text-muted small">
                          <div
                            className="spinner-border spinner-border-sm text-primary me-2"
                            role="status"
                          ></div>
                          Searching...
                        </div>
                      ) : (
                        <>
                          {suggestions.products.length === 0 &&
                          suggestions.doctors.length === 0 &&
                          suggestions.packages.length === 0 ? (
                            <div className="text-center py-3 text-muted small">
                              No results found for "
                              <strong>{searchTerm}</strong>"
                            </div>
                          ) : (
                            <div className="suggestions-list">
                              {/* Products Section */}
                              {suggestions.products.length > 0 && (
                                <div className="suggestion-group mb-2">
                                  <div className="suggestion-header text-uppercase text-muted fw-bold small mb-1">
                                    📦 Products ({suggestions.products.length})
                                  </div>
                                  {suggestions.products.map((prod) => (
                                    <div
                                      key={prod.me_id}
                                      className="suggestion-item d-flex align-items-center justify-content-between p-2 rounded cursor-pointer"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setShowSuggestions(false);
                                        navigate(
                                          `/product/${prod.me_slug || prod.me_id}`,
                                        );
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        {prod.me_image ? (
                                          <img
                                            src={prod.me_image}
                                            alt={prod.me_product_name}
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              objectFit: "cover",
                                              borderRadius: "4px",
                                            }}
                                          />
                                        ) : (
                                          <div className="bg-light p-1 rounded text-primary">
                                            📦
                                          </div>
                                        )}
                                        <span className="fw-semibold text-dark small">
                                          {prod.me_product_name}
                                        </span>
                                      </div>
                                      <span className="badge bg-light text-dark border">
                                        Product
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Doctors Section */}
                              {suggestions.doctors.length > 0 && (
                                <div className="suggestion-group mb-2">
                                  <div className="suggestion-header text-uppercase text-muted fw-bold small mb-1">
                                    👨‍⚕️ Doctors ({suggestions.doctors.length})
                                  </div>
                                  {suggestions.doctors.map((doc) => (
                                    <div
                                      key={doc.doctor_id}
                                      className="suggestion-item d-flex align-items-center justify-content-between p-2 rounded cursor-pointer"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setShowSuggestions(false);
                                        navigate(
                                          `/doctordetail/${doc.doctor_slug || doc.doctor_id}`,
                                        );
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        {doc.doctor_image ? (
                                          <img
                                            src={doc.doctor_image}
                                            alt={doc.doctor_name}
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              objectFit: "cover",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        ) : (
                                          <div className="bg-light p-1 rounded text-primary">
                                            👨‍⚕️
                                          </div>
                                        )}
                                        <div>
                                          <div className="fw-semibold text-dark small">
                                            {doc.doctor_name}
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            {doc.specialization_name ||
                                              "Specialist"}
                                          </div>
                                        </div>
                                      </div>
                                      <span className="badge bg-info-subtle text-info border">
                                        Doctor
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Packages Section */}
                              {suggestions.packages.length > 0 && (
                                <div className="suggestion-group mb-2">
                                  <div className="suggestion-header text-uppercase text-muted fw-bold small mb-1">
                                    🧪 Packages ({suggestions.packages.length})
                                  </div>
                                  {suggestions.packages.map((pkg) => (
                                    <div
                                      key={pkg.id}
                                      className="suggestion-item d-flex align-items-center justify-content-between p-2 rounded cursor-pointer"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setShowSuggestions(false);
                                        navigate(
                                          `/package?search=${encodeURIComponent(pkg.pakagename)}`,
                                        );
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="bg-light p-1 rounded text-success">
                                          🧪
                                        </div>
                                        <div>
                                          <div className="fw-semibold text-dark small">
                                            {pkg.pakagename}
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            {pkg.prices} • {pkg.nummbertest}
                                          </div>
                                        </div>
                                      </div>
                                      <span className="badge bg-success-subtle text-success border">
                                        Package
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="border-top pt-2 mt-1 text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-decoration-none fw-semibold p-0"
                                  style={{ color: "#13BAAC", fontSize: "12px" }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={handleSearchSubmit}
                                >
                                  See all results for "{searchTerm}" →
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <ul className="navbar-nav me-0 align-items-center">
                  {/* Dynamic Cart Section */}
                  {productCartCount > 0 && servicesCartCount > 0 ? (
                    // Both carts have items -> Dropdown menu
                    <li className="nav-item dropdown cart_item">
                      <a
                        href="#"
                        className="nav-link dropdown-toggle d-flex align-items-center gap-1"
                        onClick={(e) => e.preventDefault()}
                        title="Cart Options"
                      >
                        <div className="header_iconbtn position-relative">
                          <ShoppingCartOutlinedIcon />
                          <span
                            className="position-absolute badge rounded-pill bg-danger"
                            style={{
                              top: "-4px",
                              right: "-8px",
                              fontSize: "10px",
                              padding: "2px 5px",
                            }}
                          >
                            {productCartCount + servicesCartCount}
                          </span>
                          <p>
                            Cart{" "}
                            <KeyboardArrowDownIcon className="dropdown-arrow" />
                          </p>
                        </div>
                      </a>
                      <ul className="dropdown-menu dropdown-menu-end cart_heade_dropDOwn">
                        <li className="dropdown-header text-muted">
                          {" "}
                          Your Carts{" "}
                        </li>
                        <li>
                          <Link
                            to="/cart"
                            className="dropdown-item first_crt_btn"
                          >
                            <span className="fw-semibold">Product Cart</span>
                            <span className="badge rounded-pill ms-2">
                              {productCartCount}
                            </span>
                          </Link>
                        </li>

                        <li>
                          <Link to="/service-cart" className="dropdown-item">
                            <span className="fw-semibold">Services Cart</span>
                            <span className="badge rounded-pill ms-2">
                              {servicesCartCount}
                            </span>
                          </Link>
                        </li>
                      </ul>
                    </li>
                  ) : servicesCartCount > 0 ? (
                    // Only Services cart has items
                    <li className="nav-item cart_item">
                      <Link
                        className="nav-link"
                        to="/service-cart"
                        title="Services Cart"
                      >
                        <div className="header_iconbtn position-relative">
                          <ShoppingCartOutlinedIcon
                            style={{ color: "#13baac" }}
                          />
                          <span
                            className="position-absolute badge rounded-pill bg-danger"
                            style={{
                              top: "-4px",
                              right: "-8px",
                              fontSize: "10px",
                              padding: "2px 5px",
                            }}
                          >
                            {servicesCartCount}
                          </span>
                          <p>Services Cart</p>
                        </div>
                      </Link>
                    </li>
                  ) : productCartCount > 0 ? (
                    // Only Product cart has items
                    <li className="nav-item cart_item">
                      <Link
                        className="nav-link"
                        to="/cart"
                        title="Product Cart"
                      >
                        <div className="header_iconbtn position-relative">
                          <ShoppingCartOutlinedIcon />
                          <span
                            className="position-absolute badge rounded-pill bg-danger"
                            style={{
                              top: "-4px",
                              right: "-8px",
                              fontSize: "10px",
                              padding: "2px 5px",
                            }}
                          >
                            {productCartCount}
                          </span>
                          <p>Product Cart</p>
                        </div>
                      </Link>
                    </li>
                  ) : (
                    // Both carts are empty
                    <li className="nav-item cart_item">
                      <Link className="nav-link" to="/cart" title="Cart">
                        <div className="header_iconbtn">
                          <ShoppingCartOutlinedIcon />
                          <p>Cart</p>
                        </div>
                      </Link>
                    </li>
                  )}
                  <li className="nav-item">
                    {user ? (
                      <Link className="nav-link" to="/dashboard">
                        <div className="header_iconbtn">
                          <PersonOutlinedIcon />
                          <p>Hello, {user.user_first_name}</p>
                        </div>
                      </Link>
                    ) : (
                      <Link className="nav-link" to="/login">
                        <div className="header_iconbtn">
                          <PersonOutlinedIcon />
                          <p>Hello, Log in</p>
                        </div>
                      </Link>
                    )}
                  </li>

                  {/* <li className="nav-item">
                    <a className="nav-link" href="/needhelp">
                      <div className="header_iconbtn help_btn_header">
                        <p>Need Help</p>
                        <HelpIcon />
                      </div>
                    </a>
                  </li> */}
                  <li className="nav-item">
                    <a className="nav-link" href="/product-on-rent">
                      <div className="header_iconbtn help_btn_header">
                        <p>Request a Rental</p>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </div>
        <div className="header-lower">
          <nav className="custom-navbar">
            <ul className="custom-nav-list">
              {/*Home */}
              <li>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Home
                </NavLink>
              </li>

              {/*About Us */}
              <li>
                <NavLink
                  to="/about"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  About Us
                </NavLink>
              </li>

              {/* Products on Rent */}
              <li>
                <NavLink
                  to="/product-on-rent"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Products on Rent
                </NavLink>
              </li>

              {/*Contact us */}
              <li>
                <NavLink
                  to="/blogs"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Blogs
                </NavLink>
              </li>

              {/*Contact us */}
              <li>
                <NavLink
                  to="/contact"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Contact Us
                </NavLink>
              </li>

              {/* Caregiver */}
              {/* <li className="dropdown">
                <a
                  href="/"
                  className={`nav-link dropdown-toggle ${caregiverActive ? "active" : ""}`}
                >
                  {" "}
                  Caregiver <KeyboardArrowDownIcon className="dropdown-arrow" />
                </a>

                <ul className="dropdown-menu main_header_drop_box">
                  <li>
                    {" "}
                    <NavLink
                      to="/home-nursingcare"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item  active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Nursing Care</h5>
                          <span>
                            Expert nursing care for recovery & home healthcare.
                          </span>
                        </div>
                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/home-physiotherapy"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Physiotherapy</h5>
                          <span>
                            Expert nursing care for recovery & home healthcare.
                          </span>
                        </div>
                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/home-bedsideattendant"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Bedside Attendant</h5>
                          <span>
                            Expert nursing care for recovery & home healthcare.
                          </span>
                        </div>
                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>
                </ul>
              </li> */}

              {/* Products on Rent */}
              {/* <li>
                <NavLink
                  to="/product-on-rent"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Products on Rent
                </NavLink>
              </li> */}

              {/* ICU Setup */}
              {/* <li className="dropdown">
                <a
                  href="/"
                  className={`nav-link dropdown-toggle ${icuActive ? "active" : ""}`}
                >
                  ICU Setup <KeyboardArrowDownIcon className="dropdown-arrow" />
                </a>

                <ul className="dropdown-menu main_header_drop_box">
                  <li>
                    <NavLink
                      to="/icu-installation-at-home"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Home ICU</h5>
                          <span>
                            Professional ICU setup and critical care services at
                            home.
                          </span>
                        </div>

                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/icu-installation-at-hospital"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Hospital ICU</h5>
                          <span>
                            Advanced ICU facilities with expert medical
                            supervision.
                          </span>
                        </div>

                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>
                </ul>
              </li> */}

              {/* Doctor Consultation */}
              {/* <li>
                <NavLink
                  to="/doctor-consultation-services"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Doctor Consultant
                </NavLink>
              </li> */}

              {/* Diagnostic */}
              {/* <li className="dropdown">
                <a
                  href="/"
                  className={`nav-link dropdown-toggle ${diagnosticActive ? "active" : ""}`}
                >
                  {" "}
                  Diagnostic
                   <KeyboardArrowDownIcon className="dropdown-arrow" />
                </a>

                <ul className="dropdown-menu main_header_drop_box">
                  <li>
                    <NavLink
                      to="/AllPartners"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Lab Test</h5>
                          <span>
                            Book accurate diagnostic lab tests at your home.
                          </span>
                        </div>

                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/package"
                      className={({ isActive }) =>
                        isActive ? "dropdown-item active" : "dropdown-item"
                      }
                    >
                      <div className="header_dropedown_des">
                        <div className="header_drop_text">
                          <h5>Package</h5>
                          <span>
                            Choose from health checkup packages at affordable
                            prices.
                          </span>
                        </div>

                        <div className="icons_header_drop">
                          <ChevronRightIcon />
                        </div>
                      </div>
                    </NavLink>
                  </li>
                </ul>
              </li> */}

              {/* Order Medicine */}
              {/* <li>
                <NavLink
                  to="/medicine"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Medicine
                </NavLink>
              </li> */}

              {/* Emergency Services */}
              {/* <li>
                <NavLink
                  to="/emergencyservices"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Emergency Services
                </NavLink>
              </li> */}
            </ul>
          </nav>
        </div>
      </div>

      <div className="heder_mobile">
        <nav className="navbar navbar-light bg-light">
          <div className="container-fluid mobile_header_row">
            <div className="menu_btn_mobile_header">
              <button
                className="navbar-toggler new_navbar_toggler"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#mobileMenu"
              >
                <DehazeIcon />
              </button>
            </div>
            <a className="navbar-brand header_logo" href="/">
              <img src={logo} alt="Logo" />
            </a>

            <div className="header_btn_mobile">
              {/* <div className="profile_icon_mobile">
                <Link to="/cart">
                  <ShoppingCartOutlinedIcon />
                </Link>
              </div> */}

              <ul className="navbar-nav me-0 align-items-center">
                {/* Dynamic Cart Section */}
                {productCartCount > 0 && servicesCartCount > 0 ? (
                  // Both carts have items -> Dropdown menu
                  <li className="nav-item dropdown cart_item">
                    <a
                      href="#"
                      className="nav-link dropdown-toggle d-flex align-items-center gap-1"
                      onClick={(e) => e.preventDefault()}
                      title="Cart Options"
                    >
                      <div className="header_iconbtn position-relative mobile-header-cart-btn">
                        <ShoppingCartOutlinedIcon />
                        <span
                          className="position-absolute badge rounded-pill bg-danger"
                          style={{
                            top: "-4px",
                            right: "-8px",
                            fontSize: "10px",
                            padding: "2px 5px",
                          }}
                        >
                          {productCartCount + servicesCartCount}
                        </span>
                        <p>
                          <KeyboardArrowDownIcon className="dropdown-arrow" />
                        </p>
                      </div>
                    </a>
                    <ul className="dropdown-menu dropdown-menu-end cart_heade_dropDOwn">
                      <li className="dropdown-header text-muted">
                        {" "}
                        Your Carts{" "}
                      </li>
                      <li>
                        <Link
                          to="/cart"
                          className="dropdown-item first_crt_btn"
                        >
                          <span className="fw-semibold">Product Cart</span>
                          <span className="badge rounded-pill ms-2">
                            {productCartCount}
                          </span>
                        </Link>
                      </li>

                      <li>
                        <Link to="/service-cart" className="dropdown-item">
                          <span className="fw-semibold">Services Cart</span>
                          <span className="badge rounded-pill ms-2">
                            {servicesCartCount}
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </li>
                ) : servicesCartCount > 0 ? (
                  // Only Services cart has items
                  <li className="nav-item cart_item">
                    <Link
                      className="nav-link"
                      to="/service-cart"
                      title="Services Cart"
                    >
                      <div className="header_iconbtn position-relative mobile-header-cart-btn">
                        <ShoppingCartOutlinedIcon
                          style={{ color: "#13baac" }}
                        />
                        <span
                          className="position-absolute badge rounded-pill bg-danger"
                          style={{
                            top: "-4px",
                            right: "-8px",
                            fontSize: "10px",
                            padding: "2px 5px",
                          }}
                        >
                          {servicesCartCount}
                        </span>
                        <p>Services </p>
                      </div>
                    </Link>
                  </li>
                ) : productCartCount > 0 ? (
                  // Only Product cart has items
                  <li className="nav-item cart_item">
                    <Link className="nav-link" to="/cart" title="Product Cart">
                      <div className="header_iconbtn position-relative mobile-header-cart-btn">
                        <ShoppingCartOutlinedIcon />
                        <span
                          className="position-absolute badge rounded-pill bg-danger"
                          style={{
                            top: "-4px",
                            right: "-8px",
                            fontSize: "10px",
                            padding: "2px 5px",
                          }}
                        >
                          {productCartCount}
                        </span>
                        <p>Product </p>
                      </div>
                    </Link>
                  </li>
                ) : (
                  // Both carts are empty
                  <li className="nav-item cart_item">
                    <Link className="nav-link" to="/cart" title="Cart">
                      <div className="header_iconbtn mobile-header-cart-btn">
                        <ShoppingCartOutlinedIcon />
                      </div>
                    </Link>
                  </li>
                )}
              </ul>

              <div className="profile_icon_mobile">
                <Link to="/dashboard">
                  <PersonOutlinedIcon />
                </Link>
              </div>
            </div>

            <div
              className="offcanvas offcanvas-start"
              tabIndex="-1"
              id="mobileMenu"
            >
              <div className="offcanvas-header">
                <a className="navbar-brand header_logo" href="/">
                  <img src={logo} alt="Logo" />
                </a>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="offcanvas"
                ></button>
              </div>

              <div className="offcanvas-body mobile_h_sec">
                <ul className="navbar-nav">
                  {/* Caregiver */}
                  {/* <li className="nav-item dropdown">
                    <a
                      href="/"
                      className="nav-link dropdown-toggle"
                      data-bs-toggle="dropdown"
                      onClick={(e) => e.preventDefault()}
                    >
                      {" "}
                      Caregiver{" "}
                    </a>

                    <ul className="dropdown-menu">
                      <li>
                        {" "}
                        <NavLink
                          to="/home-nursingcare"
                          className="dropdown-item"
                        >
                          <ChevronRightIcon /> Nursing Care{" "}
                        </NavLink>{" "}
                      </li>

                      <li>
                        {" "}
                        <NavLink
                          to="/home-physiotherapy"
                          className="dropdown-item"
                        >
                          <ChevronRightIcon /> Physiotherapy{" "}
                        </NavLink>{" "}
                      </li>

                      <li>
                        {" "}
                        <NavLink
                          to="/home-bedsideattendant"
                          className="dropdown-item"
                        >
                          <ChevronRightIcon /> Bedside Attendant{" "}
                        </NavLink>{" "}
                      </li>
                    </ul>
                  </li> */}
                  {/* Products On Rent */}

                  {/* Home */}
                  <li className="nav-item">
                    {" "}
                    <NavLink to="/" className="nav-link">
                      {" "}
                      Home
                    </NavLink>{" "}
                  </li>
                  <li className="nav-item">
                    {" "}
                    <NavLink to="/product-on-rent" className="nav-link">
                      Products On Rent
                    </NavLink>{" "}
                  </li>

                  {/* ICU */}
                  {/* <li className="nav-item dropdown">
                    <a
                      href="/"
                      className="nav-link dropdown-toggle"
                      data-bs-toggle="dropdown"
                      onClick={(e) => e.preventDefault()}
                    >
                      {" "}
                      ICU Setup{" "}
                    </a>

                    <ul className="dropdown-menu">
                      <li>
                        {" "}
                        <NavLink
                          to="/icu-installation-at-home"
                          className="dropdown-item"
                        >
                          <ChevronRightIcon /> Home ICU{" "}
                        </NavLink>{" "}
                      </li>

                      <li>
                        {" "}
                        <NavLink
                          to="/icu-installation-at-hospital"
                          className="dropdown-item"
                        >
                          <ChevronRightIcon /> Hospital ICU{" "}
                        </NavLink>{" "}
                      </li>
                    </ul>
                  </li> */}
                  {/* Doctor Consultation */}
                  {/* <li className="nav-item">
                    {" "}
                    <NavLink to="/doctorconsult" className="nav-link">
                      {" "}
                      Doctor Consultant
                    </NavLink>{" "}
                  </li> */}

                  {/* Diagnostic */}
                  {/* <li className="nav-item dropdown">
                    <a
                      href="/"
                      className="nav-link dropdown-toggle"
                      data-bs-toggle="dropdown"
                      onClick={(e) => e.preventDefault()}
                    >
                      Diagnostic
                    </a>

                    <ul className="dropdown-menu">
                      <li>
                        {" "}
                        <NavLink to="/AllPartners" className="dropdown-item">
                          <ChevronRightIcon /> Lab Test{" "}
                        </NavLink>{" "}
                      </li>

                      <li>
                        {" "}
                        <NavLink to="/package" className="dropdown-item">
                          <ChevronRightIcon /> Package{" "}
                        </NavLink>{" "}
                      </li>
                    </ul>
                  </li> */}

                  {/* Order Medicine */}
                  {/* <li className="nav-item">
                    {" "}
                    <NavLink to="/medicine" className="nav-link">
                      Order Medicine
                    </NavLink>
                  </li> */}

                  <li className="nav-item">
                    {" "}
                    <NavLink to="/about" className="nav-link">
                      {" "}
                      About Us{" "}
                    </NavLink>{" "}
                  </li>

                  {/* Contact */}
                  <li className="nav-item">
                    {" "}
                    <NavLink to="/blogs" className="nav-link">
                      {" "}
                      Blogs
                    </NavLink>{" "}
                  </li>

                  {/* Contact */}
                  <li className="nav-item">
                    {" "}
                    <NavLink to="/contact" className="nav-link">
                      {" "}
                      Contact Us
                    </NavLink>{" "}
                  </li>
                  <div className="follow_us_icons">
                    <p>Follow Us : </p>
                    <div className="mobile_header_mediaicons">
                      <a href="https://www.facebook.com/caresathome/">
                        <FacebookIcon />
                      </a>
                      <a href="https://www.linkedin.com/company/caresathome?original_referer=https%3A%2F%2Fcaresathome%2Ecom%2F&originalSubdomain=in">
                        <LinkedInIcon />
                      </a>
                      <a href="https://www.instagram.com/caresathome/">
                        <InstagramIcon />
                      </a>
                      {/* <a href="">
                        <WhatsAppIcon />
                      </a> */}
                      <a href="https://www.youtube.com/channel/UCIEkL7Xb8G_9dltD4nyCkHg">
                        <YouTubeIcon />
                      </a>
                      <a href="https://x.com/cares_at_home">
                        <XIcon />
                      </a>
                    </div>
                  </div>
                </ul>
              </div>
            </div>
          </div>

          {/* <div className="mobile_location_header_sec only_mobile_location">
            <div className="location-header">
              <div className="icon-location">
                <LocationOnIcon />
              </div>

              <div className="dropdown">
                <button
                  className="location-btn"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <div className="location-title-new">
                    Location
                    <KeyboardArrowDownIcon />
                  </div>
                  <div className="location-address-new">
                    15 Corner, Sector 10 Shopping Center Vijay Path Crossing,
                    Madhyam Marg, Mansarovar, Jaipur, Rajasthan 302020
                  </div>
                </button>

                <ul className="dropdown-menu">
                  <li>
                    <a className="dropdown-item" href="/">
                      Location 1
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href="/">
                      Location 2
                    </a>
                  </li>
                  <li>
                    <a className="dropdown-item" href="/">
                      Location 3
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div> */}

          {/* <div className="mobile_search_bar">
                <div className="serch-form-header">
                    <form className="search-form">
                        <input type="search" placeholder="Search Products.........." className="search-input" />
                        <button type="submit" className="search-btn"> <SearchIcon /> </button>
                    </form>
                </div>
            </div> */}
        </nav>
      </div>

      <LoginModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
}

export default Header;
